/**
 * PropertyOverview Component
 *
 * Welcome section with property description, contact info,
 * and image gallery slider with overlay navigation.
 * Features: chevron icons, crossfade transitions, dot indicators,
 * click-to-open lightbox, background preloading of all gallery images.
 *
 * Gallery uses 10 slot groups (not a collection binding) for per-image
 * editorial control in the editor.
 */

import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  useContentToken,
  useSlotGroup,
  usePropertyAddressUrl,
  usePropertyField,
  EditableToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
// @ts-ignore
import { VibesitesImage } from '@vibesites/ui-primitives';
// @ts-ignore
import { ChevronLeftIcon, ChevronRightIcon, CameraIcon } from '@vibesites/assets';
import { PhotoGallery } from './PhotoGallery';
import { formatPhone } from '../utils/format-phone';

// ============================================================================
// Slot group definitions
// ============================================================================

const addressSlotDef: SlotGroupDefinition = {
  label: 'Address',
  fields: {
    address: {
      label: 'Address',
      type: 'text',
      defaultField: 'property_full_address',
      defaultValue: '123 Main Street',
    },
    // URL has no defaultField — defaults to usePropertyAddressUrl() at render
    // time. Editor overrides still win.
    url: {
      label: 'URL',
      type: 'url',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_blank',
    },
  },
};

// 10 gallery photo slot groups
const photo1Def: SlotGroupDefinition = {
  label: 'Gallery Photo 1',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'Property photo 1' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[0].description', defaultValue: '' },
  },
};

const photo2Def: SlotGroupDefinition = {
  label: 'Gallery Photo 2',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: 'Property photo 2' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[1].description', defaultValue: '' },
  },
};

const photo3Def: SlotGroupDefinition = {
  label: 'Gallery Photo 3',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: 'Property photo 3' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[2].description', defaultValue: '' },
  },
};

const photo4Def: SlotGroupDefinition = {
  label: 'Gallery Photo 4',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text', defaultValue: 'Property photo 4' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[3].description', defaultValue: '' },
  },
};

const photo5Def: SlotGroupDefinition = {
  label: 'Gallery Photo 5',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text', defaultValue: 'Property photo 5' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[4].description', defaultValue: '' },
  },
};

const photo6Def: SlotGroupDefinition = {
  label: 'Gallery Photo 6',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text', defaultValue: 'Property photo 6' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[5].description', defaultValue: '' },
  },
};

const photo7Def: SlotGroupDefinition = {
  label: 'Gallery Photo 7',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text', defaultValue: 'Property photo 7' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[6].description', defaultValue: '' },
  },
};

const photo8Def: SlotGroupDefinition = {
  label: 'Gallery Photo 8',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text', defaultValue: 'Property photo 8' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[7].description', defaultValue: '' },
  },
};

const photo9Def: SlotGroupDefinition = {
  label: 'Gallery Photo 9',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text', defaultValue: 'Property photo 9' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[8].description', defaultValue: '' },
  },
};

const photo10Def: SlotGroupDefinition = {
  label: 'Gallery Photo 10',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text', defaultValue: 'Property photo 10' },
    description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[9].description', defaultValue: '' },
  },
};

const photoDefs = [photo1Def, photo2Def, photo3Def, photo4Def, photo5Def, photo6Def, photo7Def, photo8Def, photo9Def, photo10Def];
const photoKeys = [
  'propertyoverview.photo_1', 'propertyoverview.photo_2', 'propertyoverview.photo_3',
  'propertyoverview.photo_4', 'propertyoverview.photo_5', 'propertyoverview.photo_6',
  'propertyoverview.photo_7', 'propertyoverview.photo_8', 'propertyoverview.photo_9',
  'propertyoverview.photo_10',
] as const;

// Gallery image sizes descriptor — matches 2-column layout (50% on desktop)
const GALLERY_SIZES = '(max-width: 768px) 100vw, (max-width: 1400px) 50vw, 700px';

// ============================================================================
// Component
// ============================================================================

export function PropertyOverview() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);

  const sectionTitle = useContentToken('propertyoverview.title');
  const propertyName = useContentToken('propertyoverview.property_name');
  const phoneToken = useContentToken('propertyoverview.phone');
  const trackingPhone = usePropertyField('property_call_tracking_phone') as string | null;
  // Fallback chain: editor override / custom_ppc_tracking_phone_number → property_call_tracking_phone
  const phone = (phoneToken && phoneToken.trim()) ? phoneToken : (trackingPhone || '');
  // Address slot group — `address` text defaults to property_full_address.
  // The `url` field has no defaultField, so we fall back to the address-URL
  // hook (google_place_url → google_business_url → property_full_address →
  // lat/lon). Editor-set overrides on the slot still win.
  const address = useSlotGroup('propertyoverview.address', addressSlotDef);
  const propertyAddressUrl = usePropertyAddressUrl();

  // Resolve all 10 photo slot groups
  const photo1 = useSlotGroup('propertyoverview.photo_1', photo1Def);
  const photo2 = useSlotGroup('propertyoverview.photo_2', photo2Def);
  const photo3 = useSlotGroup('propertyoverview.photo_3', photo3Def);
  const photo4 = useSlotGroup('propertyoverview.photo_4', photo4Def);
  const photo5 = useSlotGroup('propertyoverview.photo_5', photo5Def);
  const photo6 = useSlotGroup('propertyoverview.photo_6', photo6Def);
  const photo7 = useSlotGroup('propertyoverview.photo_7', photo7Def);
  const photo8 = useSlotGroup('propertyoverview.photo_8', photo8Def);
  const photo9 = useSlotGroup('propertyoverview.photo_9', photo9Def);
  const photo10 = useSlotGroup('propertyoverview.photo_10', photo10Def);

  const allSlotGroups = [photo1, photo2, photo3, photo4, photo5, photo6, photo7, photo8, photo9, photo10];

  // Build display gallery from slot groups, filtering out empty entries
  const displayGallery = useMemo(() => {
    return allSlotGroups
      .map((sg, i) => ({
        src: sg?.image || '',
        alt: sg?.alt || `Property photo ${i + 1}`,
        description: sg?.description || '',
        slotGroupKey: photoKeys[i],
        slotGroupDef: photoDefs[i],
      }))
      .filter(p => p.src);
  }, [photo1, photo2, photo3, photo4, photo5, photo6, photo7, photo8, photo9, photo10]);

  // Preload all gallery images in the background after initial render
  useEffect(() => {
    if (displayGallery.length === 0) return;
    const idle = window.requestIdleCallback || ((cb: IdleRequestCallback) => setTimeout(cb, 200));
    const handle = idle(() => {
      displayGallery.forEach(p => {
        if (p.src) {
          const img = new Image();
          img.src = p.src;
        }
      });
    });
    return () => {
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(handle as number);
      }
    };
  }, [displayGallery]);

  const goToSlide = useCallback((nextIdx: number) => {
    setCurrentSlide(nextIdx);
  }, []);

  const nextSlide = useCallback(() => {
    setCurrentSlide(prev => (prev + 1) % displayGallery.length);
  }, [displayGallery.length]);

  const prevSlide = useCallback(() => {
    setCurrentSlide(prev => (prev - 1 + displayGallery.length) % displayGallery.length);
  }, [displayGallery.length]);

  const lightboxPhotos = displayGallery.map(p => ({ src: p.src, alt: p.alt }));

  return (
    <section className="bg-background py-16 md:py-24" id="overview" style={{ scrollMarginTop: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }}>
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">
        {/* Content column */}
        <div className="flex flex-col gap-6">
          <EditableToken path="propertyoverview.title" type="text" label="Section Title" as="div">
            <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-bold font-heading text-foreground leading-tight uppercase m-0">
              {sectionTitle}{' '}
              <EditableToken path="propertyoverview.property_name" type="text" label="Property Name" as="span">
                <span className="text-foreground">{propertyName}</span>
              </EditableToken>
              , your new home.
            </h2>
          </EditableToken>

          {/* Phone */}
          <EditableToken
            path="propertyoverview.phone"
            type="phone"
            label="Phone Number"
            as="div"
            style={{ display: 'inline-block' }}
          >
            <a href={`tel:${phone?.replace(/[^+\d]/g, '')}`} className="text-foreground no-underline text-2xl font-medium font-accent">
              {formatPhone(phone)}
            </a>
          </EditableToken>

          {/* Address */}
          <EditableSlotGroup groupPath="propertyoverview.address" definition={addressSlotDef}>
            <a
              href={address.url || propertyAddressUrl || '#'}
              target={address._target || '_blank'}
              rel="noopener noreferrer"
              className="text-muted-foreground text-base underline"
            >
              {address.address}
            </a>
          </EditableSlotGroup>

          {/* Description (richtext) */}
          <RichTextContent
            path="propertyoverview.description"
            label="Description"
            as="p"
            className="text-base leading-relaxed text-foreground m-0"
          />
        </div>

        {/* Gallery column */}
        <div className="flex flex-col gap-4">
          {displayGallery.length > 0 ? (
            <>
              <div
                className="relative rounded-lg overflow-hidden aspect-[16/10] bg-neutral-900 group"
              >
                {/* All 10 photo slot groups rendered for editor */}
                <EditableSlotGroup groupPath="propertyoverview.photo_1" definition={photo1Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_2" definition={photo2Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_3" definition={photo3Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_4" definition={photo4Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_5" definition={photo5Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_6" definition={photo6Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_7" definition={photo7Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_8" definition={photo8Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_9" definition={photo9Def}>
                  <div className="hidden" />
                </EditableSlotGroup>
                <EditableSlotGroup groupPath="propertyoverview.photo_10" definition={photo10Def}>
                  <div className="hidden" />
                </EditableSlotGroup>

                {/* All images stacked — CSS transition handles crossfade */}
                {displayGallery.map((photo, i) => (
                  <div
                    key={i}
                    className="absolute inset-0 z-[2]"
                    style={{
                      opacity: i === currentSlide ? 1 : 0,
                      transition: 'opacity 0.35s ease',
                      pointerEvents: i === currentSlide ? 'auto' : 'none',
                    }}
                  >
                    <VibesitesImage
                      url={photo.src}
                      alt={photo.alt || 'Property image'}
                      fill
                      objectFit="cover"
                      sizes={GALLERY_SIZES}
                      quality={80}
                      priority={i < 3}
                    />
                  </div>
                ))}


                {/* Full-area lightbox trigger — above images (z-[3]), below nav buttons (z-[5]).
                    A real <button> here avoids nesting interactive controls inside a
                    role="button" div, which violates WCAG 4.1.1 and the HTML spec. */}
                <button
                  className="absolute inset-0 z-[3] w-full h-full bg-transparent border-none p-0 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-white"
                  onClick={() => setLightboxOpen(true)}
                  aria-label={`View photo gallery – ${displayGallery.length} photo${displayGallery.length !== 1 ? 's' : ''}`}
                />

                {/* "View gallery" badge */}
                <div className="absolute top-3 right-3 z-[4] flex items-center gap-1.5 bg-black/50 backdrop-blur-sm text-white text-xs font-medium px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none">
                  <CameraIcon size={13} />
                  <span>{displayGallery.length} Photos</span>
                </div>

                {/* Overlay navigation buttons */}
                {displayGallery.length > 1 && (
                  <>
                    <button
                      className="absolute top-1/2 left-3 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-sm cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-white hover:shadow-md border-none opacity-70 md:opacity-0 md:group-hover:opacity-100 text-foreground"
                      onClick={(e) => { e.stopPropagation(); prevSlide(); }}
                      aria-label="Previous image"
                    >
                      <ChevronLeftIcon size={18} />
                    </button>
                    <button
                      className="absolute top-1/2 right-3 -translate-y-1/2 z-[5] w-10 h-10 rounded-full bg-white/80 backdrop-blur-sm shadow-sm cursor-pointer flex items-center justify-center transition-all duration-200 hover:bg-white hover:shadow-md border-none opacity-70 md:opacity-0 md:group-hover:opacity-100 text-foreground"
                      onClick={(e) => { e.stopPropagation(); nextSlide(); }}
                      aria-label="Next image"
                    >
                      <ChevronRightIcon size={18} />
                    </button>
                  </>
                )}
              </div>

              {/* Dot indicators */}
              {displayGallery.length > 1 && (
                <div className="flex items-center justify-center gap-1.5">
                    {displayGallery.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => goToSlide(index)}
                        className="w-2 h-2 rounded-full border-none cursor-pointer p-0"
                        style={{
                          backgroundColor: index === currentSlide ? 'var(--vibe-color-secondary)' : 'var(--vibe-color-border, #d1d5db)',
                          transform: index === currentSlide ? 'scale(1.25)' : 'scale(1)',
                          transition: 'background-color 0.3s ease, transform 0.3s ease',
                        }}
                        aria-label={`Go to image ${index + 1}`}
                      />
                    ))}
                </div>
              )}
            </>
          ) : (
            <div className="aspect-[16/10] bg-muted rounded-lg flex items-center justify-center text-muted-foreground">
              <span>No gallery images available</span>
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxPhotos.length > 0 && (
        <PhotoGallery
          photos={lightboxPhotos}
          open={lightboxOpen}
          initialIndex={currentSlide}
          onClose={() => setLightboxOpen(false)}
        />
      )}

    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.title': {
      label: 'Section Title',
      type: 'text',
      defaultValue: 'Welcome to',
    },
    'propertyoverview.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
      defaultValue: 'Your New Home',
    },
    'propertyoverview.description': {
      label: 'Description',
      type: 'richtext',
      suggestedFields: ['property_short_description', 'property_long_description'],
      defaultValue:
        '<p>At Ventura Lofts, room to live is the difference. Our <span data-type="field-token" data-field="property_bedrooms_range"></span> bedroom homes offer some of the largest apartment layouts in the area — spanning <span data-type="field-token" data-field="property_sqft_min"></span> to <span data-type="field-token" data-field="property_sqft_max"></span> sq. ft. — so you get more space than you\'ll find at many nearby communities. Explore open floor plans designed for modern Houston living, then schedule a tour to see the difference for yourself.</p>',
    },
    'propertyoverview.phone': {
      label: 'Phone Number',
      type: 'phone',
      defaultField: 'custom_ppc_tracking_phone_number',
      suggestedFields: ['custom_ppc_tracking_phone_number', 'property_call_tracking_phone', 'property_phone'],
    },
  },
  slotGroups: {
    'propertyoverview.address': {
      label: 'Address',
      fields: {
        address: {
          label: 'Address',
          type: 'text',
          defaultField: 'property_full_address',
          defaultValue: '123 Main Street',
        },
        // URL has no defaultField — defaults to usePropertyAddressUrl() at
        // render time. Editor overrides still win.
        url: { label: 'URL', type: 'url' },
        _target: { label: 'Target', type: 'text', defaultValue: '_blank' },
      },
    },
    'propertyoverview.photo_1': {
      label: 'Gallery Photo 1',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text', defaultValue: 'Property photo 1' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[0].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_2': {
      label: 'Gallery Photo 2',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text', defaultValue: 'Property photo 2' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[1].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_3': {
      label: 'Gallery Photo 3',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text', defaultValue: 'Property photo 3' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[2].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_4': {
      label: 'Gallery Photo 4',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text', defaultValue: 'Property photo 4' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[3].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_5': {
      label: 'Gallery Photo 5',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text', defaultValue: 'Property photo 5' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[4].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_6': {
      label: 'Gallery Photo 6',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[5].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[5].alt_text', defaultValue: 'Property photo 6' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[5].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_7': {
      label: 'Gallery Photo 7',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[6].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[6].alt_text', defaultValue: 'Property photo 7' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[6].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_8': {
      label: 'Gallery Photo 8',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[7].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[7].alt_text', defaultValue: 'Property photo 8' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[7].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_9': {
      label: 'Gallery Photo 9',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[8].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[8].alt_text', defaultValue: 'Property photo 9' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[8].description', defaultValue: '' },
      },
    },
    'propertyoverview.photo_10': {
      label: 'Gallery Photo 10',
      fields: {
        image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[9].image' },
        alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[9].alt_text', defaultValue: 'Property photo 10' },
        description: { label: 'Description', type: 'text', defaultField: 'photo_gallery[9].description', defaultValue: '' },
      },
    },
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
